/**
 * Context for player data overrides (wage, est valuation).
 *
 * Provides override values and setter functions to deeply nested
 * table cell components without prop drilling through TanStack Table.
 * @module features/squad-planning/contexts/PlayerOverridesContext
 */

import React, { createContext, useContext } from "react";

import type { PlayerOverride } from "../stores/playerOverridesStore";

/** Context value shape */
interface PlayerOverridesContextValue {
  /** Map of player ID to overrides */
  readonly overrides: Readonly<Record<string, PlayerOverride>>;
  /** Update a player's wage override */
  readonly setWage: (playerId: string, wage: number | null) => void;
  /** Update a player's est valuation override */
  readonly setEstValuation: (
    playerId: string,
    estValuation: number | null
  ) => void;
}

const PlayerOverridesContext = createContext<PlayerOverridesContextValue>({
  overrides: {},
  setWage: () => {},
  setEstValuation: () => {},
});

export const PlayerOverridesProvider = PlayerOverridesContext.Provider;

/**
 * Hook to access player overrides from context.
 * @returns Override values and setter functions
 */
export function usePlayerOverridesContext(): PlayerOverridesContextValue {
  return useContext(PlayerOverridesContext);
}
